import { QuoteClientView } from './client-view/QuoteClientView';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { addGuestOrder, guestOrderPath } from '../../lib/guest-session';
import { readGuestScope } from '../../lib/guest-token';

interface QuoteDetailPageProps {
	/**
	 * Rendered inside the guest shell: same view, minus the affordance that
	 * leads into a platform the guest isn't meant to browse.
	 */
	guest?: boolean;
}

/**
 * The client's own quote page.
 *
 * The view itself lives in `./client-view`, built on the same `@codezo/quote-detail`
 * modules as the admin's own quote page so the two sides can't drift. What stays
 * here is what only this file can answer: which currency the user picked, who
 * they are, and where an order lives in these routes — which differs for a guest.
 */
export function QuoteDetailPage({ guest = false }: QuoteDetailPageProps = {}) {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { user, token } = useAuth();
	/* Guest scope comes from the session, not from this tab — see
	   `readGuestScope`. A guest reached in a second tab is still a guest. */
	const guestScope = readGuestScope(token);

	// A guest has no platform routes — keep them inside the guest shell, and
	// remember the order so a reload can bring them back to it.
	const openOrder = (orderId: string, options?: { autoOpenPayment?: boolean }) => {
		const state = options?.autoOpenPayment ? { autoOpenPayment: true } : undefined;
		if (guestScope) {
			addGuestOrder(orderId);
			navigate(guestOrderPath(orderId), { state });
			return;
		}
		navigate(`/orders/${orderId}`, { state });
	};

	/* An admin looking at the guest view. The screens stay identical — that is
	   the point of the preview — but every write is refused server-side, so the
	   controls that would attempt one are disabled and say so. */
	const readOnlyReason = guestScope?.preview
		? 'Preview session — this action is disabled'
		: undefined;

	if (!id) return null;

	return (
		/* The view brings its own page container — it sizes the sticky header and
		   the section area together — so this wrapper is only for the crumb row
		   above it. */
		<div className="pt-6">
			{!guest && (
				<div className="mx-auto mb-2 flex max-w-[var(--content-max)] items-center gap-4 px-4 sm:px-6 md:px-8">
					<button
						onClick={() => navigate('/quotes')}
						className="flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
					>
						<ArrowLeft size={14} />
						Quotes
					</button>
				</div>
			)}
			<QuoteClientView
				key={id}
				quoteId={id}
				onOpenOrder={openOrder}
				fallbackOrganizationId={user?.organizationId}
				currentOrganizationUser={user?.organizationUser}
				readOnlyReason={readOnlyReason}
			/>
		</div>
	);
}
